import { Plugin, PluginKey } from '@tiptap/pm/state'
import { type EditorView } from '@tiptap/pm/view'
import { compressImage, getCompressedMimeType } from './imageCompression'
import { saveImage, IMAGE_URL_PREFIX } from '../db/images'
import { logger } from './logger'
import { captureError } from './errorReporting'
import { track, AnalyticsEvents } from './mixpanel'

const log = logger('ImageHandler')

const MAX_RAW_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

export interface ImageHandlerOptions {
  /** Returns the current note ID for associating images */
  getNoteId: () => string | null
  /** Map to track blob URL -> image ID for persistence */
  blobToIdMap: Map<string, string>
}

/**
 * Creates a ProseMirror plugin that intercepts paste and drop events
 * containing images, compresses them, stores in IndexedDB, and inserts
 * an image node into the editor.
 */
export function createImageHandlerPlugin(options: ImageHandlerOptions): Plugin {
  return new Plugin({
    key: new PluginKey('imageHandler'),
    props: {
      handlePaste(view: EditorView, event: ClipboardEvent) {
        const files = getImageFiles(event.clipboardData?.files)
        if (files.length === 0) return false

        event.preventDefault()
        for (const file of files) {
          handleImageFile(view, file, options)
        }
        return true
      },

      handleDrop(view: EditorView, event: DragEvent) {
        const files = getImageFiles(event.dataTransfer?.files)
        if (files.length === 0) return false

        event.preventDefault()
        for (const file of files) {
          handleImageFile(view, file, options)
        }
        return true
      },
    },
  })
}

/**
 * Extract valid image files from a FileList.
 */
function getImageFiles(fileList: FileList | undefined | null): File[] {
  if (!fileList) return []
  const files: File[] = []
  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i]
    if (ACCEPTED_TYPES.includes(file.type)) {
      files.push(file)
    }
  }
  return files
}

/**
 * Process a single image file: validate, compress, store, and insert into editor.
 */
export async function handleImageFile(
  view: EditorView,
  file: File,
  options: ImageHandlerOptions
): Promise<void> {
  const noteId = options.getNoteId()
  if (!noteId) {
    log.warn('No active note, cannot save image')
    return
  }

  // Reject files over 10MB raw
  if (file.size > MAX_RAW_SIZE) {
    log.warn(`Image too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max is 10MB.`)
    return
  }

  try {
    // Show immediate preview with raw blob URL
    const previewUrl = URL.createObjectURL(file)
    insertImage(view, previewUrl)

    // Compress in background
    const compressed = await compressImage(file)
    const mimeType = getCompressedMimeType(file.type)

    // Store in IndexedDB
    const imageId = await saveImage(noteId, compressed, mimeType)
    track(AnalyticsEvents.IMAGE_PASTED, { size: file.size, type: file.type, compressedSize: compressed.size })
    const permanentUrl = `${IMAGE_URL_PREFIX}${imageId}`

    // Create a new blob URL from the compressed image for display
    const compressedBlobUrl = URL.createObjectURL(compressed)

    // Track the mapping: blob URL -> image ID (for persistence when saving)
    options.blobToIdMap.set(compressedBlobUrl, imageId)
    // Also track the preview URL in case it's still in the HTML
    options.blobToIdMap.set(previewUrl, imageId)

    // Replace the preview URL with the compressed blob URL in the editor HTML
    const { state } = view
    const { tr } = state
    let replaced = false
    state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs.src === previewUrl) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          src: compressedBlobUrl,
          'data-image-id': imageId,
          'data-permanent-url': permanentUrl,
        })
        replaced = true
      }
    })

    if (replaced) {
      view.dispatch(tr)
    }
  } catch (error) {
    captureError(error, { source: 'imageHandler', extra: { noteId, fileSize: file.size, fileType: file.type } })
    track(AnalyticsEvents.IMAGE_UPLOAD_FAILED, { size: file.size, type: file.type })
  }
}

/**
 * Insert an image node at the current cursor position.
 */
function insertImage(view: EditorView, src: string): void {
  const { schema } = view.state
  const imageNode = schema.nodes.image
  if (!imageNode) {
    log.warn('Image node type not found in schema')
    return
  }

  const node = imageNode.create({ src })
  const transaction = view.state.tr.replaceSelectionWith(node)
  view.dispatch(transaction)
}